import { LayoutMode } from '@lved/shared-core';
import {
    FrontendApplication,
    FrontendApplicationContribution,
    ApplicationShell as TheiaApplicationShell,
    codicon
} from '@theia/core/lib/browser';
import { TabBarToolbarContribution, TabBarToolbarRegistry } from '@theia/core/lib/browser/shell/tab-bar-toolbar';
import { BOTTOM_AREA_ID } from '@theia/core/lib/browser/shell/theia-dock-panel';
import { WidgetManager } from '@theia/core/lib/browser/widget-manager';
import { Widget } from '@theia/core/lib/browser/widgets';
import { MenuContribution, MenuModelRegistry } from '@theia/core/lib/common';
import { CommandContribution, CommandRegistry } from '@theia/core/lib/common/command';
import { inject, injectable } from '@theia/core/shared/inversify';
import { AppShell } from './application-shell';
import { FILTERED_BUILTIN_WIDGET_IDS } from './contribution-filters';

/** Widget IDs for Theia built-in panels. */
const CHAT_VIEW_WIDGET_ID = 'chat-view-widget';
const EXPLORER_VIEW_CONTAINER_ID = 'explorer-view-container';

/** Widget IDs visible only in design mode left sidebar. */
const DESIGN_ONLY_PANEL_IDS: readonly string[] = ['widget:lvgl-design-panel', 'widget:lvgl-screens-navigator'];

/** Widget IDs visible only in code mode left sidebar. */
const CODE_ONLY_PANEL_IDS: readonly string[] = [EXPLORER_VIEW_CONTAINER_ID];

/** Command IDs for the bottom panel toggle toolbar buttons. */
const PANEL_COLLAPSE_BOTTOM_PANEL = 'lvgl:collapseBottomPanel';
const PANEL_EXPAND_BOTTOM_PANEL = 'lvgl:expandBottomPanel';
const HIDDEN_BOTTOM_TOOLBAR_ITEM_IDS: readonly string[] = ['terminal:manager-maximize-bottom-panel'];
const LAYOUT_SWITCH_OVERLAY_DURATION_MS: number = 200;

/**
 * Bridges the shared @lved/ui LayoutMode store to the Theia shell, driving:
 *  - body[data-lvgl-layout] attribute consumed by branding.css
 *  - Active left-sidebar panel switched per mode
 *  - Main panel hidden/shown via ApplicationShell.setMainPanelVisible()
 *  - Bottom panel collapsed in design mode and restored in code mode
 *  - Minimize / restore button added to every bottom panel tab's toolbar
 */
@injectable()
export class LayoutModeContribution
    implements FrontendApplicationContribution, CommandContribution, TabBarToolbarContribution, MenuContribution
{
    @inject(TheiaApplicationShell)
    protected readonly shell!: TheiaApplicationShell;

    @inject(WidgetManager)
    protected readonly widgetManager!: WidgetManager;

    @inject(TabBarToolbarRegistry)
    protected readonly tabBarToolbarRegistry!: TabBarToolbarRegistry;

    private currentMode: LayoutMode = LayoutMode.CODE;
    private layoutSwitchTimeout: number | undefined;

    private get appShell(): AppShell {
        return this.shell as AppShell;
    }

    async onDidInitializeLayout(_app: FrontendApplication): Promise<void> {
        this.unregisterDefaultBottomActions();
        // Apply lrPanel / mainRightPanel / mainBottomPanel sizes (either restored
        // from storage or the hardcoded defaults: 280 px left, equal main / right).
        this.appShell.applyPanelSizes();

        await this.removeFilteredBuiltinWidgets();

        // Move AI chat to left sidebar (overrides its default 'right' area).
        await this.ensureAiChatInLeftSidebar();

        // Subscribe to the shared UI store and apply the initial layout mode.
        await this.subscribeToLayoutMode();
    }

    // -------------------------------------------------------------------------
    // CommandContribution
    // -------------------------------------------------------------------------

    registerCommands(commands: CommandRegistry): void {
        commands.registerCommand(
            { id: PANEL_COLLAPSE_BOTTOM_PANEL, label: 'Minimize Bottom Panel' },
            {
                execute: () => this.shell.collapsePanel('bottom'),
                isVisible: (widget) =>
                    widget instanceof Widget &&
                    widget.parent?.id === BOTTOM_AREA_ID &&
                    !this.appShell.isBottomPanelCollapsed()
            }
        );

        commands.registerCommand(
            { id: PANEL_EXPAND_BOTTOM_PANEL, label: 'Restore Bottom Panel' },
            {
                execute: () => this.shell.expandPanel('bottom'),
                isVisible: (widget) =>
                    widget instanceof Widget &&
                    widget.parent?.id === BOTTOM_AREA_ID &&
                    this.appShell.isBottomPanelCollapsed()
            }
        );
    }

    registerMenus(_menus: MenuModelRegistry): void {
        // Intentionally no-op: keep MenuContribution contract for existing DI bindings.
    }

    protected unregisterDefaultBottomActions(): void {
        for (const itemId of HIDDEN_BOTTOM_TOOLBAR_ITEM_IDS) {
            this.tabBarToolbarRegistry.unregisterItem(itemId);
        }
    }

    // -------------------------------------------------------------------------
    // TabBarToolbarContribution
    // -------------------------------------------------------------------------

    registerToolbarItems(toolbar: TabBarToolbarRegistry): void {
        const onDidChange = this.appShell.onDidToggleBottomPanel;

        toolbar.registerItem({
            id: PANEL_COLLAPSE_BOTTOM_PANEL,
            command: PANEL_COLLAPSE_BOTTOM_PANEL,
            icon: codicon('chevron-down'),
            tooltip: 'Minimize Bottom Panel',
            onDidChange,
            priority: 2
        });

        toolbar.registerItem({
            id: PANEL_EXPAND_BOTTOM_PANEL,
            command: PANEL_EXPAND_BOTTOM_PANEL,
            icon: codicon('chevron-up'),
            tooltip: 'Restore Bottom Panel',
            onDidChange,
            priority: 2
        });
    }

    // -------------------------------------------------------------------------
    // AI chat placement
    // -------------------------------------------------------------------------

    private async removeFilteredBuiltinWidgets(): Promise<void> {
        for (const widgetId of FILTERED_BUILTIN_WIDGET_IDS) {
            try {
                if (this.widgetManager.tryGetWidget(widgetId)) {
                    await this.shell.closeWidget(widgetId);
                }
            } catch {
                // Widget may already be gone or not restorable.
            }
        }
    }

    private async ensureAiChatInLeftSidebar(): Promise<void> {
        try {
            let widget = this.widgetManager.tryGetWidget(CHAT_VIEW_WIDGET_ID);
            if (!widget) {
                widget = await this.widgetManager.getOrCreateWidget(CHAT_VIEW_WIDGET_ID).catch(() => undefined);
            }
            if (widget && this.shell.getAreaFor(widget) !== 'left') {
                await this.shell.addWidget(widget, { area: 'left' });
            }
        } catch {
            // AI chat extension may not be installed – ignore.
        }
    }

    // -------------------------------------------------------------------------
    // Layout mode subscription
    // -------------------------------------------------------------------------

    private async subscribeToLayoutMode(): Promise<void> {
        try {
            const { useUIStore } = await import('@lved/ui');
            const initial = useUIStore.getState().layoutMode;
            this.applyLayoutMode(initial, /* force */ true);
            useUIStore.subscribe((state) => this.applyLayoutMode(state.layoutMode));
        } catch {
            // @lved/ui store unavailable – stay in code mode.
            this.applyLayoutMode(LayoutMode.CODE, /* force */ true);
        }
    }

    private applyLayoutMode(mode: LayoutMode, force: boolean = false): void {
        if (!force && this.currentMode === mode) return;

        const prefersReducedMotion: boolean = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (force || prefersReducedMotion) {
            this.commitLayoutMode(mode);
            return;
        }

        document.body.classList.add('lvgl-layout-switching');

        if (this.layoutSwitchTimeout !== undefined) {
            window.clearTimeout(this.layoutSwitchTimeout);
        }

        this.currentMode = mode;
        this.layoutSwitchTimeout = window.setTimeout((): void => {
            this.commitLayoutMode(mode);
            this.layoutSwitchTimeout = undefined;

            requestAnimationFrame((): void => {
                document.body.classList.remove('lvgl-layout-switching');
            });
        }, LAYOUT_SWITCH_OVERLAY_DURATION_MS);
    }

    private commitLayoutMode(mode: LayoutMode): void {
        this.currentMode = mode;
        document.body.setAttribute('data-lvgl-layout', mode);

        if (mode === LayoutMode.DESIGN) {
            void this.shell.collapsePanel('bottom');
            this.activateDesignMode();
            return;
        }

        void this.shell.expandPanel('bottom');
        this.activateCodeMode();
    }

    // -------------------------------------------------------------------------
    // Design mode
    // -------------------------------------------------------------------------

    private activateDesignMode(): void {
        // Collapse the Monaco editor panel to 0 width (stays in DOM so
        // Monaco keeps its state and the preview can track the active editor).
        this.appShell.setMainPanelVisible(false);
        // Sync body attribute so the preview toggle button shows the correct label.
        document.body.setAttribute('data-lvgl-main', 'hidden');

        // Switch active left panel to the design panel.
        queueMicrotask(async () => {
            try {
                const designWidget = this.widgetManager.tryGetWidget('widget:lvgl-design-panel');
                if (designWidget) await this.shell.activateWidget(designWidget.id);
            } catch {
                /* ignore */
            }
        });
    }

    // -------------------------------------------------------------------------
    // Code mode
    // -------------------------------------------------------------------------

    private activateCodeMode(): void {
        // Restore the Monaco editor panel.
        this.appShell.setMainPanelVisible(true);
        // Keep body attribute in sync for CSS and preview toggle label.
        document.body.setAttribute('data-lvgl-main', 'visible');

        // Switch active left panel to the file explorer.
        queueMicrotask(async () => {
            try {
                const explorerWidget = this.widgetManager.tryGetWidget(EXPLORER_VIEW_CONTAINER_ID);
                if (explorerWidget) await this.shell.activateWidget(explorerWidget.id);
            } catch {
                /* ignore */
            }
        });
    }
}

// Re-export so that CSS rule authoring can reference these IDs in one place.
export { CODE_ONLY_PANEL_IDS, DESIGN_ONLY_PANEL_IDS };
